import type { IMcpConnection, IMcpTool } from '@query'

// Three per-tool permission states (Claude-connector style):
//  - allow:    Luca runs the tool automatically.
//  - approval: Luca pauses for the user's confirmation first (-> approval_tools).
//  - disabled: Luca can't use the tool at all (excluded from allowed_tools).
export type PermState = 'allow' | 'approval' | 'disabled'

export type ToolGroupKey = 'read' | 'write' | 'other'

export interface ToolGroup {
  key: ToolGroupKey
  tools: IMcpTool[]
}

export interface ApprovalPatch {
  approval_tools: string[]
  allowed_tools?: string[]
  clear_allowed_tools?: boolean
}

// Reconstruct each tool's state from the stored connection. allowed_tools is a whitelist
// (null = all allowed); a tool absent from a non-null whitelist is disabled.
export const deriveStates = (connection: IMcpConnection): Record<string, PermState> => {
  const approval = new Set(connection.approval_tools ?? [])
  const allowed = connection.allowed_tools
  const allowedSet = allowed ? new Set(allowed) : null
  const states: Record<string, PermState> = {}
  for (const tool of connection.available_tools ?? []) {
    if (allowedSet && !allowedSet.has(tool.name)) states[tool.name] = 'disabled'
    else if (approval.has(tool.name)) states[tool.name] = 'approval'
    else states[tool.name] = 'allow'
  }
  return states
}

// Group by the standard MCP readOnlyHint. Servers that don't annotate land in 'other'; the
// UI collapses a lone 'other' group into a single flat "Tools" list (no invented split).
export const groupTools = (tools: IMcpTool[]): ToolGroup[] => {
  const buckets: Record<ToolGroupKey, IMcpTool[]> = { read: [], write: [], other: [] }
  for (const tool of tools) {
    if (tool.read_only === true) buckets.read.push(tool)
    else if (tool.read_only === false) buckets.write.push(tool)
    else buckets.other.push(tool)
  }
  const order: ToolGroupKey[] = ['read', 'write', 'other']
  return order.filter(key => buckets[key].length > 0).map(key => ({ key, tools: buckets[key] }))
}

// Prefer the server-provided display title (MCP standard) and fall back to the raw tool name.
export const toolDisplayName = (tool: IMcpTool): string =>
  tool.title && tool.title.trim() ? tool.title.trim() : tool.name

// The single common state across a group, or null when the group is mixed.
export const commonState = (
  tools: IMcpTool[],
  states: Record<string, PermState>
): PermState | null => {
  if (tools.length === 0) return null
  const first = states[tools[0].name]
  return tools.every(tool => states[tool.name] === first) ? first : null
}

// Translate the per-tool state map back into the stored fields. When nothing is disabled we
// clear the whitelist (all allowed, incl. tools the server may add later).
export const computePatch = (
  tools: IMcpTool[],
  states: Record<string, PermState>
): ApprovalPatch => {
  const approval_tools = tools.filter(tool => states[tool.name] === 'approval').map(t => t.name)
  const hasDisabled = tools.some(tool => states[tool.name] === 'disabled')
  if (hasDisabled) {
    const allowed_tools = tools
      .filter(tool => states[tool.name] !== 'disabled')
      .map(tool => tool.name)
    return { approval_tools, allowed_tools }
  }
  return { approval_tools, clear_allowed_tools: true }
}

export const statesEqual = (
  a: Record<string, PermState>,
  b: Record<string, PermState>
): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) if (a[key] !== b[key]) return false
  return true
}
